import React from "react";
import "bootstrap-icons/font/bootstrap-icons.css";

const getIconClassName = (icon) => `bi ${icon.startsWith("bi-") ? icon : `bi-${icon}`}`;

/* One clickable card for a single sub-module. The whole card is the
   button — icon, label, and description are just its content. */
export function ModuleCard({ icon, color, label, description, onClick }) {
  return (
    <button type="button" className="mop-card" style={{ "--mop-accent": color }} onClick={onClick}>
      <div className="mop-card-icon" style={{ background: `${color}16`, color }}>
        <i className={getIconClassName(icon)}></i>
      </div>
      <div className="mop-card-body">
        <span className="mop-card-label">{label}</span>
        {description && <span className="mop-card-desc">{description}</span>}
      </div>
      <i className="bi bi-chevron-right mop-card-arrow"></i>
    </button>
  );
}

const CardGrid = ({ items, onNavigate }) => (
  <div className="mop-grid">
    {items.map((item, idx) => (
      <ModuleCard
        // Usually item.key doubles as the React key, but a group can have
        // two items that intentionally navigate to the same key (e.g. two
        // doors into one shared page) — item.id disambiguates those.
        key={item.id || `${item.key}-${idx}`}
        icon={item.icon}
        color={item.color}
        label={item.label}
        description={item.description}
        onClick={() => onNavigate?.(item.key)}
      />
    ))}
  </div>
);

/* Level 2 of the nav: a module's overview page. Shows the module's title
   + description, then its child items as ModuleCards — each navigating to
   that item's existing page via onNavigate.

   Items may optionally carry a `section` label (e.g. "Andi Makuru"); any
   item without one renders in the main grid, and items sharing a section
   are grouped under their own subheading further down the page, in the
   order that section first appears. Groups with no sectioned items render
   exactly one flat grid, same as before. */
export default function ModuleOverviewPage({ icon, color, label, description, items, onNavigate }) {
  const primaryItems = items.filter((item) => !item.section);
  const sectionNames = [...new Set(items.filter((item) => item.section).map((item) => item.section))];

  return (
    <div className="mop-root">
      <style>{MODULE_OVERVIEW_CSS}</style>
      <div className="mop-header">
        <div className="mop-header-icon" style={{ background: `${color}18`, color }}>
          <i className={getIconClassName(icon)}></i>
        </div>
        <div className="mop-header-copy">
          <h1 className="mop-title">{label}</h1>
          {description && <p className="mop-desc">{description}</p>}
        </div>
      </div>

      {primaryItems.length > 0 && <CardGrid items={primaryItems} onNavigate={onNavigate} />}

      {sectionNames.map((sectionName) => (
        <div key={sectionName} className="mop-section">
          <div className="mop-section-label">{sectionName}</div>
          <CardGrid items={items.filter((item) => item.section === sectionName)} onNavigate={onNavigate} />
        </div>
      ))}
    </div>
  );
}

const MODULE_OVERVIEW_CSS = `
  .mop-root { animation: mopFadeUp 0.3s ease both; }
  @keyframes mopFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  .mop-header {
    display: flex; align-items: center; gap: 16px;
    margin-bottom: 22px; padding-bottom: 18px;
    border-bottom: 1px solid #e2e8f0;
  }
  .mop-header-icon {
    width: 56px; height: 56px; min-width: 56px; border-radius: 14px;
    display: flex; align-items: center; justify-content: center; font-size: 1.55rem;
    box-shadow: inset 0 0 0 1px rgba(0,0,0,0.04);
  }
  .mop-header-copy { min-width: 0; }
  .mop-title { margin: 0; font-size: 1.28rem; font-weight: 800; color: #003380; letter-spacing: -0.01em; }
  .mop-desc { margin: 4px 0 0; font-size: 0.85rem; color: #5b6b82; line-height: 1.5; }

  .mop-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 14px; }
  @media (max-width: 900px) { .mop-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 560px) { .mop-grid { grid-template-columns: 1fr; gap: 12px; } }

  .mop-section { margin-top: 24px; }
  .mop-section-label {
    display: flex; align-items: center; gap: 10px;
    font-size: 0.72rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    color: #94a3b8; margin-bottom: 12px;
  }
  .mop-section-label::after { content: ""; flex: 1; height: 1px; background: #e2e8f0; }

  .mop-card {
    display: flex; align-items: flex-start; gap: 14px;
    padding: 18px; border-radius: 12px;
    background: #ffffff; border: 1px solid #e6ebf2;
    border-left: 3px solid var(--mop-accent, #0047AB);
    text-align: left; cursor: pointer; font-family: inherit;
    box-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.03);
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  }
  .mop-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 24px rgba(0,51,128,0.14), 0 2px 6px rgba(0,51,128,0.08);
    border-color: var(--mop-accent, #94a3b8);
  }
  .mop-card:active { transform: translateY(-1px); }
  .mop-card-icon {
    width: 44px; height: 44px; min-width: 44px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; font-size: 1.2rem;
    flex-shrink: 0; margin-top: 1px; transition: transform 0.18s ease;
  }
  .mop-card:hover .mop-card-icon { transform: scale(1.06); }
  .mop-card-body { min-width: 0; display: flex; flex-direction: column; gap: 4px; padding-top: 2px; }
  .mop-card-label { font-size: 0.92rem; font-weight: 700; color: #0f172a; line-height: 1.3; }
  .mop-card-desc {
    font-size: 0.76rem; color: #8592a6; line-height: 1.45;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .mop-card-arrow { margin-left: auto; margin-top: 4px; font-size: 0.78rem; color: #cbd5e1; flex-shrink: 0; transition: all 0.18s ease; }
  .mop-card:hover .mop-card-arrow { color: var(--mop-accent, #94a3b8); transform: translateX(3px); }
`;
